import React from "react";
import { ClosureDisclosure, Row } from "./ReviewBlockParts";
import {
  COMMITMENT_CHECKOUT_COPY,
  endBehaviorSentence,
  formatCommitmentDate,
  formatEuros,
  paymentsSentence,
  type CommitmentReview,
} from "@lemonadetraining/web-shared/utils/commitmentPlans";
import { formatUnitLabel } from "@lemonadetraining/web-shared/utils/membershipLabels";

/**
 * What one commitment costs, spelled out before it is bought.
 *
 * A definition list, because that is genuinely what this is — labelled facts
 * about one contract — and because it gives a screen reader user the term with
 * its value instead of a run of unattached numbers.
 *
 * Every row is conditional on having a real figure behind it. A commitment the
 * gym's current configuration could not confirm shows fewer rows and says so;
 * it never shows a plausible number we could not source.
 */

interface CommitmentReviewBlockProps {
  title: string;
  review: CommitmentReview;
  /** Distinguishes the blocks when a cart holds more than one commitment. */
  testIdSuffix: string;
}

const CommitmentReviewBlock = ({ title, review, testIdSuffix }: CommitmentReviewBlockProps) => {
  const headingId = `commitment-review-heading-${testIdSuffix}`;

  return (
    <section
      aria-labelledby={headingId}
      data-testid={`commitment-review-${testIdSuffix}`}
      className="mt-4 rounded-lg border p-4"
    >
      <h3 id={headingId} className="text-sm font-semibold">
        {COMMITMENT_CHECKOUT_COPY.reviewHeading}
      </h3>
      <p className="text-muted-foreground mb-2 text-xs">{title}</p>

      <dl className="divide-y">
        <Row
          label={COMMITMENT_CHECKOUT_COPY.termLabel}
          value={formatUnitLabel(review.commitmentMonths, "month")}
          testId={`commitment-term-${testIdSuffix}`}
        />

        {review.total !== null && (
          <Row
            label={COMMITMENT_CHECKOUT_COPY.totalLabel}
            value={formatEuros(review.total)}
            testId={`commitment-total-${testIdSuffix}`}
          />
        )}

        {/* Shown only when committing genuinely costs less than paying month to
            month. A zero or negative "saving" is not a saving, and dressing one
            up as a benefit is the kind of small dishonesty this whole screen
            exists to prevent. */}
        {review.savings !== null && review.savings > 0 && (
          <Row
            label={COMMITMENT_CHECKOUT_COPY.savingsLabel}
            value={`${formatEuros(review.savings)} vs paying monthly`}
            testId={`commitment-savings-${testIdSuffix}`}
          />
        )}

        <Row
          label={COMMITMENT_CHECKOUT_COPY.paymentsLabel}
          value={paymentsSentence(review)}
          testId={`commitment-payments-${testIdSuffix}`}
        />

        <Row
          label={COMMITMENT_CHECKOUT_COPY.endsLabel}
          value={formatCommitmentDate(review.endDate)}
          testId={`commitment-end-date-${testIdSuffix}`}
        />

        <Row
          label={COMMITMENT_CHECKOUT_COPY.afterLabel}
          value={endBehaviorSentence(review)}
          testId={`commitment-end-behavior-${testIdSuffix}`}
        />
      </dl>

      {/* The buyer is looking at figures the gym may since have changed. Saying
          so is the difference between a stale screen and a misleading one. */}
      {!review.fromFreshConfig && (
        <p
          className="text-muted-foreground mt-2 text-xs"
          data-testid={`commitment-stale-note-${testIdSuffix}`}
        >
          {COMMITMENT_CHECKOUT_COPY.staleConfigNote}
        </p>
      )}

      {review.priceChanged && (
        <p
          className="mt-2 text-xs font-medium"
          data-testid={`commitment-price-changed-${testIdSuffix}`}
        >
          {COMMITMENT_CHECKOUT_COPY.priceChangedNote}
        </p>
      )}

      <ClosureDisclosure testIdSuffix={`commitment-${testIdSuffix}`} />
    </section>
  );
};

export default CommitmentReviewBlock;
